import type { CaptureResult, PostHog } from 'posthog-js';

/**
 * Exception messages that are pure noise — browser quirks and cross-origin
 * redactions with no actionable signal. Dropped from both autocaptured
 * (`capture_exceptions`) and manually-reported exceptions. Browser-extension
 * origins are excluded separately by PostHog's
 * `error_tracking.captureExtensionExceptions: false` default.
 */
const IGNORED_MESSAGES: RegExp[] = [
  /ResizeObserver loop/i,
  /^Script error\.?$/,
  /^Non-Error promise rejection captured/,
  // Old cached bundles on Safari <15.4 / Chrome <92 before the .at() polyfill.
  /\.at is not a function/,
  // Browser-extension content scripts (chrome.runtime.sendMessage). The
  // extension-origin filter misses these because the throwing frame is
  // main-world/anonymous, not a chrome-extension:// URL.
  /runtime\.sendMessage/i,
  // iOS in-app webview bridge injected by Instagram/Facebook/etc. browsers.
  /window\.webkit\.messageHandlers/,
];

function isIgnored(message: string | undefined): boolean {
  return !!message && IGNORED_MESSAGES.some((re) => re.test(message));
}

/**
 * PostHog `before_send` filter: drop noisy `$exception` events, pass everything
 * else (analytics, pageviews, replay) through untouched.
 */
export function filterEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;
  const list = (event.properties?.['$exception_list'] ?? []) as { value?: string; type?: string }[];
  if (list.some((e) => isIgnored(e.value) || isIgnored(e.type))) return null;
  return event;
}

let client: PostHog | null = null;
const pending: { error: unknown; componentStack: string | undefined }[] = [];
const MAX_PENDING = 20;

function forward(error: unknown, componentStack?: string): void {
  if (!client) return;
  const message = error instanceof Error ? error.message : String(error);
  if (isIgnored(message)) return;
  client.captureException(
    error,
    componentStack ? { $exception_react_component_stack: componentStack } : undefined,
  );
}

/** Called once posthog-js has loaded; flushes errors captured before init. */
export function registerErrorClient(posthog: PostHog): void {
  client = posthog;
  for (const { error, componentStack } of pending.splice(0)) forward(error, componentStack);
}

/**
 * Report a handled exception (e.g. from React's `<ErrorBoundary>` or the
 * `createRoot` error hooks). PostHog loads on idle, so errors thrown before
 * init are queued (capped) and flushed on `registerErrorClient` — boot-time
 * render errors aren't lost. Unhandled window errors are autocaptured by
 * `capture_exceptions` once PostHog is live.
 */
export function captureException(error: unknown, componentStack?: string): void {
  if (client) {
    forward(error, componentStack);
    return;
  }
  if (pending.length < MAX_PENDING) pending.push({ error, componentStack });
}
